/**
 * Safe Cart Drawer Currency Formatter
 * Only runs when cart drawer is actually opened/updated
 * Minimal interference with cart app operations
 */

(function() {
  'use strict';

  // Configuration
  const SPECIAL_CURRENCIES = ['AUD', 'NZD', 'CAD'];
  const CART_DRAWER_SELECTOR = '#upCart';
  
  // Track if we've already processed
  let lastProcessTime = 0;
  
  // Get current currency
  function getCurrentCurrency() {
    return window.Shopify?.currency?.active || 
           document.querySelector('[data-currency]')?.dataset.currency ||
           'USD';
  }

  // Format price according to our custom logic
  function formatPrice(priceText, currency) {
    if (!priceText || !currency) return priceText;
    
    // Check if it's a special currency
    if (!SPECIAL_CURRENCIES.includes(currency.toUpperCase())) return priceText;
    
    // More comprehensive price detection and replacement
    const pricePatterns = [
      // Standard dollar amounts
      /\$\s*([\d,]+\.?\d*)/g,  // $99.00, $63.00
      // Existing currency codes
      /([\d,]+\.?\d*)\s*AUD/gi, // 99.00 AUD
      /([\d,]+\.?\d*)\s*NZD/gi, // 99.00 NZD  
      /([\d,]+\.?\d*)\s*CAD/gi, // 99.00 CAD
      // Dollar with possible currency codes
      /\$\s*([\d,]+\.?\d*)\s*(AUD|NZD|CAD)?/gi, // $99.00 AUD
      // Special patterns for checkout and threshold messages
      /(\$[\d,]+\.?\d*)/g, // Simple $XX.XX anywhere in text
      // Bullet or dot separator in checkout buttons
      /•\s*\$\s*([\d,]+\.?\d*)/g, // • $87.00
      /\•\s*\$\s*([\d,]+\.?\d*)/g, // bullet • $87.00
    ];
    
    let formattedText = priceText;
    
    pricePatterns.forEach(pattern => {
      formattedText = formattedText.replace(pattern, (match, amount, currencyCode) => {
        if (amount) {
          // Preserve any prefixes like "• " for checkout buttons
          const prefix = match.substring(0, match.indexOf('$'));
          return `${prefix}${currency.toUpperCase()} ${amount}`;
        }
        return match;
      });
    });
    
    return formattedText;
  }

  // Walk through text nodes only to preserve HTML structure
  function formatTextNode(node, currency) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent;
      if (text && /[\d.,]/.test(text)) {
        const formatted = formatPrice(text, currency);
        if (formatted !== text) {
          node.textContent = formatted;
        }
      }
    }
  }

  // Reset processing markers (useful for page loads)
  function resetProcessingMarkers() {
    try {
      const cartDrawer = document.querySelector(CART_DRAWER_SELECTOR);
      if (cartDrawer && cartDrawer.shadowRoot) {
        const processedElements = cartDrawer.shadowRoot.querySelectorAll('[data-currency-processed]');
        processedElements.forEach(element => {
          element.removeAttribute('data-currency-processed');
        });
      }
    } catch (e) {
      // Ignore errors
    }
  }

  // Safe formatting function
  function safeFormatPrices(resetMarkers = false) {
    // Throttle to prevent excessive calls
    const now = Date.now();
    if (now - lastProcessTime < 1000) return;
    lastProcessTime = now;
    
    // Reset markers if requested (useful for page load)
    if (resetMarkers) {
      resetProcessingMarkers();
    }
    
    try {
      const cartDrawer = document.querySelector(CART_DRAWER_SELECTOR);
      if (!cartDrawer || !cartDrawer.shadowRoot) return;
      
      const currency = getCurrentCurrency();
      if (!SPECIAL_CURRENCIES.includes(currency.toUpperCase())) return;
      
      const shadowRoot = cartDrawer.shadowRoot;
      
      // Target only specific price-containing elements but preserve their structure
      const priceSelectors = [
        '[class*="price"]',
        '[class*="total"]', 
        '[class*="amount"]',
        '[class*="cost"]',
        '[class*="checkout"]',
        '[class*="threshold"]',
        '[class*="progress"]',
        '[class*="shipping"]',
        '[class*="away"]',
        '[class*="free"]',
        '.money',
        'button',
        'span',
        'div',
        'p'
      ];
      
      // Additional comprehensive search for any element containing dollar signs
      const additionalElements = shadowRoot.querySelectorAll('*');
      const elementsWithDollarSigns = Array.from(additionalElements).filter(el => {
        return el.textContent && el.textContent.includes('$') && 
               !el.hasAttribute('data-currency-processed') &&
               el.children.length === 0; // Only leaf elements to avoid duplicates
      });
      
      // Process regular selector elements
      priceSelectors.forEach(selector => {
        const elements = shadowRoot.querySelectorAll(selector);
        elements.forEach(element => {
          processElement(element, currency);
        });
      });
      
      // Process additional elements containing dollar signs
      elementsWithDollarSigns.forEach(element => {
        processElement(element, currency);
      });
      
    } catch (e) {
      // Silently fail to avoid breaking cart
      console.warn('Currency format error:', e);
    }
  }
  
  // Helper function to process individual elements
  function processElement(element, currency) {
    try {
      // Skip if already processed
      if (element.hasAttribute('data-currency-processed')) return;
      
      // Process all text nodes within this element
      const walker = document.createTreeWalker(
        element,
        NodeFilter.SHOW_TEXT,
        null,
        false
      );
      
      const textNodes = [];
      let node;
      while (node = walker.nextNode()) {
        textNodes.push(node);
      }
      
      textNodes.forEach(textNode => {
        formatTextNode(textNode, currency);
      });
      
      // Mark as processed
      element.setAttribute('data-currency-processed', 'true');
    } catch (e) {
      // Skip problematic elements
    }
  }

  // Manual trigger function
  function triggerFormat() {
    setTimeout(safeFormatPrices, 1000);
  }

  // Format on page load and repeated checks for cart drawer
  function initializePageFormatting() {
    // Initial check after page load with fresh processing
    setTimeout(() => safeFormatPrices(true), 2000);
    
    // Additional checks in case cart drawer loads later
    setTimeout(() => safeFormatPrices(true), 4000);
    setTimeout(() => safeFormatPrices(true), 6000);
    
    // Check periodically for first 30 seconds in case cart app loads slowly
    let checkCount = 0;
    const maxChecks = 15; // 30 seconds total (15 checks * 2 seconds)
    
    const loadChecker = setInterval(() => {
      checkCount++;
      
      const cartDrawer = document.querySelector(CART_DRAWER_SELECTOR);
      if (cartDrawer && cartDrawer.shadowRoot) {
        safeFormatPrices(true); // Reset markers on successful load
        clearInterval(loadChecker); // Stop checking once we find and format
      }
      
      if (checkCount >= maxChecks) {
        clearInterval(loadChecker); // Stop after max checks
      }
    }, 2000);
  }

  // Initialize with minimal interference
  function initialize() {
    // Start page load formatting
    initializePageFormatting();
    
    // Only add event listeners for manual triggering
    document.addEventListener('click', function(e) {
      // Check if cart-related element was clicked
      if (e.target.closest('[class*="cart"]') || 
          e.target.closest('[class*="Cart"]') ||
          e.target.closest('#upCart')) {
        triggerFormat();
      }
    });
    
    // Listen for cart updates
    document.addEventListener('cart:updated', triggerFormat);
    
    // Expose manual trigger
    window.formatCartDrawerCurrency = triggerFormat;
    
    console.log('Safe Cart Drawer Currency Formatter initialized');
  }

  // Start when ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }

  // Also trigger on window load as a fallback for late-loading apps
  window.addEventListener('load', () => {
    setTimeout(() => safeFormatPrices(true), 1000);
  });

})(); 